import _ from "lodash";
import React from "react";
import {
  SquareFilled,
  SinglePointRubricOutlined,
  UserArrowOutlined,
  ClipboardPencilLinesOutlined,
  TeacherShareOutlined,
  PlaceholderdotsOutlined,
  ClipboardArrowOutlined,
  AssessmentApplicableOutlined,
  SettingsOutlined,
  GridOutlined,
  InsightsOutlined,
  TickCircleOpenOutlined,
  PinOutlined,
  DashboardOutlined,
  LanguageOutlined,
} from "@toddle-edu/ds-icons";

import { colors } from "Constants";
import { formatDate, generateRandomIdV2, getAcademicYearLabel } from "Utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import { ADMIN, STAFF } from "Constants/stringConstants";
import { getSettingValue } from "Utils/ConfigurationUtils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { goToRelativeRoute } from "modules/Services";
import { isUserPreferredAIVoiceLanguageModuleWiseEnabled } from "AppComponents/ConfigureAIVoice/modules/Utils";
import {
  MODULES,
  ACCESS_LEVELS,
  RULE_OPTIONS,
  getIsAdminByRole,
} from "lib/staffPermissionUtils";
import {
  ENABLE_BEHAVIOUR_MODULE_PERM,
  ENABLE_BEHAVIOUR_MODULE_IN_ADMIN_PORTAL,
  ADMIN_PORTAL_ACCESS_PERMISSION,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ENABLE_BEHAVIOUR_SETUP_CARD,
  BEHAVIOUR_SETUP_ACCESS_PERMISSION,
  BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,
  ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
  ENABLE_DESCRIPTION_TEMPLATE_IN_BEHAVIOUR,
  BEHAVIOUR_CAN_CREATE_INCIDENT_PERMISSION,
  BEHAVIOUR_CAN_VIEW_INCIDENT_PERMISSION,
  BEHAVIOUR_CAN_EDIT_INCIDENT_PERMISSION,
  BEHAVIOUR_CAN_VIEW_INCIDENT_AND_CONFIDENTIAL_PERMISSION,
  BEHAVIOUR_CAN_EDIT_INCIDENT_AND_VIEW_CONFIDENTIAL_PERMISSION,
  BEHAVIOUR_CAN_EDIT_INCIDENT_AND_CONFIDENTIAL_PERMISSION,
  ENABLE_BEHAVIOUR_DATE_RANGE_FILTER,
  ENABLE_INCIDENT_NOTIFICATION_TOGGLE,
  ACCOUNT_LEVEL_BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,
  PROGRAMME_LEVEL_BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,
  BEHAVIOUR_RNP_ACCOUNT_LEVEL_MODULE_SETTING,
  BEHAVIOUR_RNP_PROGRAMME_LEVEL_MODULE_SETTING,
} from "Constants/permissionConstants";
import {
  getOrganizationId,
  getSelectedAcademicYearId,
  getUserCurriculumPrograms,
  getAcademicYearStartDate,
  getAcademicYearEndDate,
  getSelectedAcademicYear,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import { getAdminPanelModuleAccessCurriculumIds } from "Administrator/modules/Utils";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getDateMomentObjectCustom } from "Utils/DateTimeHelpers";
import moment from "moment";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";
import { store } from "store/createStore";
import {
  getIsBehaviourRnPModuleConsumptionEnabled,
  getIsMultipleHomeroomAdvisorsEnabled,
} from "Utils/ACLStoreUtils";
import { checkIsHomePageFeatureEnabled } from "Platform/routes/HomePage/modules/HomePageUtils";
import { HOME_PAGE_FEATURE_BEHAVIOR_MANAGEMENT } from "Teacher/routes/Courses/components/TeacherCourses/Constants";
import {
  getCanAccessBehaviourDashboardInRnPOrg,
  getIsBehaviourModuleEnabledInModuleSettingsAndRnP,
} from "Utils/BehaviourUtils";

export const DROPDOWN_REFETCH_THRESHOLD = 10;
export const NEW_BEHAVIOUR_ADMIN_PREFIX = "NEW_BEHAVIOUR_ADMIN_";
export const DEFAULT_SHOULD_NOTIFY_VALUE = false;
export const DEFAULT_SHOULD_NOTIFY_VALUE_FOR_REFERRAL = true;

// Action type constants

export const INCIDENT_ACTION_STATUS = {
  ADDED: "ADDED",
  APPLIED: "APPLIED",
};

export const REFERRAL_ACTION_KEY = "REFERRAL";

export const INCIDENT_STATE = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
  RESOLVED: "RESOLVED",
};

export const INCIDENT_STATE_CONFIG = {
  [INCIDENT_STATE.DRAFT]: {
    tagColor: "yellow",
    label: "common:draft",
  },
  [INCIDENT_STATE.PUBLISHED]: {
    tagColor: "blue",
    label: "common:published",
  },
  [INCIDENT_STATE.RESOLVED]: {
    tagColor: "teal",
    label: "common:resolved",
  },
};

export const ACCESSIBILITY_TYPE = {
  STAFF: "STAFF",
  ALL_STAFF: "ALL_STAFF",
  INVOLVED_STUDENT: "INVOLVED_STUDENT",
  FAMILY_MEMBERS: "FAMILY_MEMBERS",
  HOMEROOM_ADVISOR: "HOMEROOM_ADVISOR",
  ADMINS: "ADMINS",
  BEHAVIOUR_ADMINS: "BEHAVIOUR_ADMINS",
  ROLE: "RNP_ROLE",
  BEHAVIOUR_RNP_ROLE: "BEHAVIOUR_RNP_ROLE",
};

export const ACCESSIBILITY_TYPE_PERMISSION_MAP = {
  [ACCESSIBILITY_TYPE.ADMINS]: ["Behaviour:EnableAdminsInIncidentSharing"],
  [ACCESSIBILITY_TYPE.ALL_STAFF]: ["Behaviour:EnableAllStaffInIncidentSharing"],
  [ACCESSIBILITY_TYPE.FAMILY_MEMBERS]: [
    "Behaviour:EnableFamilyInIncidentSharing",
  ],
  [ACCESSIBILITY_TYPE.INVOLVED_STUDENT]: [
    "Behaviour:EnableStudentInIncidentSharing",
  ],
};

export const ACCESSIBILITY_ACCESS_TYPE = {
  CAN_EDIT: "CAN_EDIT",
  CAN_VIEW: "CAN_VIEW",
  NO_ACCESS: "NO_ACCESS",
  REFERRED: "REFERRED",
  OWNER: "OWNER",
};

export const ACCESSIBILITY_LEVEL_MAP = {
  [ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS]: 0,
  [ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW]: 1,
  [ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT]: 2,
  [ACCESSIBILITY_ACCESS_TYPE.REFERRED]: 3,
  [ACCESSIBILITY_ACCESS_TYPE.OWNER]: 3,
};

export const ACCESSIBILITY_DETAIL_TYPE = {
  INCIDENT_DETAIL: "INCIDENT_DETAIL",
  CONFIDENTIAL_DETAIL: "CONFIDENTIAL_DETAIL",
};

export const BEHAVIOUR_RNP_PERMISSION = {
  MODULE_SETTING: BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,
  CAN_CREATE_INCIDENT: BEHAVIOUR_CAN_CREATE_INCIDENT_PERMISSION,
  CAN_VIEW_INCIDENT: BEHAVIOUR_CAN_VIEW_INCIDENT_PERMISSION,
  CAN_EDIT_INCIDENT: BEHAVIOUR_CAN_EDIT_INCIDENT_PERMISSION,
  CAN_VIEW_INCIDENT_AND_CONFIDENTIAL: BEHAVIOUR_CAN_VIEW_INCIDENT_AND_CONFIDENTIAL_PERMISSION,
  CAN_EDIT_INCIDENT_AND_VIEW_CONFIDENTIAL: BEHAVIOUR_CAN_EDIT_INCIDENT_AND_VIEW_CONFIDENTIAL_PERMISSION,
  CAN_EDIT_INCIDENT_AND_CONFIDENTIAL: BEHAVIOUR_CAN_EDIT_INCIDENT_AND_CONFIDENTIAL_PERMISSION,
};

export const BEHAVIOUR_RNP_FOR_ACCOUNT_CP = {
  MODULE_SETTING: BEHAVIOUR_RNP_ACCOUNT_LEVEL_MODULE_SETTING,
  CAN_VIEW_BEHAVIOUR_DASHBOARD: ACCOUNT_LEVEL_BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,
  CAN_CREATE_INCIDENT: "BehaviourDashboardV2ForAccount:CanCreateIncident",
  CAN_VIEW_ALL_INCIDENTS: "BehaviourDashboardV2ForAccount:CanViewAllIncidents",
  CAN_EDIT_ALL_INCIDENTS: "BehaviourDashboardV2ForAccount:CanEditAllIncidents",
  CAN_VIEW_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES:
    "BehaviourDashboardV2ForAccount:CanViewAllIncidentsAndConfidentialNotes",
  CAN_EDIT_ALL_INCIDENTS_AND_VIEW_CONFIDENTIAL_NOTES:
    "BehaviourDashboardV2ForAccount:CanEditAllIncidentsAndViewConfidentialNotes",
  CAN_EDIT_DELETE_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES:
    "BehaviourDashboardV2ForAccount:CanEditDeleteAllIncidentsAndConfidentialNotes",
};

export const BEHAVIOUR_RNP_FOR_PROGRAMME_CP = {
  MODULE_SETTING: BEHAVIOUR_RNP_PROGRAMME_LEVEL_MODULE_SETTING,

  CAN_VIEW_STUDENT_PROFILE_AND_BEHAVIOUR_FOR_ALL_STUDENTS:
    "BehaviourDashboardV2ForProgramme:CanViewStudentProfileAndBehaviourForAllStudents",

  CAN_VIEW_BEHAVIOUR_DASHBOARD: PROGRAMME_LEVEL_BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,

  CAN_CREATE_INCIDENTS_FOR_THEIR_STUDENTS:
    "BehaviourDashboardV2ForProgramme:CanCreateIncidentsForTheirStudents",
  CAN_CREATE_INCIDENTS_FOR_ALL:
    "BehaviourDashboardV2ForProgramme:CanCreateIncidentsForAll",

  CAN_VIEW_INCIDENTS_FOR_THEIR_STUDENTS:
    "BehaviourDashboardV2ForProgramme:CanViewIncidentsForTheirStudents",
  CAN_VIEW_ALL_INCIDENTS:
    "BehaviourDashboardV2ForProgramme:CanViewAllIncidents",

  CAN_EDIT_INCIDENTS_FOR_THEIR_STUDENTS:
    "BehaviourDashboardV2ForProgramme:CanEditIncidentsForTheirStudents",
  CAN_EDIT_ALL_INCIDENTS:
    "BehaviourDashboardV2ForProgramme:CanEditAllIncidents",

  CAN_VIEW_INCIDENTS_AND_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS:
    "BehaviourDashboardV2ForProgramme:CanViewIncidentsAndConfidentialNotesForTheirStudents",
  CAN_VIEW_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES:
    "BehaviourDashboardV2ForProgramme:CanViewAllIncidentsAndConfidentialNotes",

  CAN_EDIT_INCIDENTS_AND_VIEW_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS:
    "BehaviourDashboardV2ForProgramme:CanEditIncidentsAndViewConfidentialNotesForTheirStudents",
  CAN_EDIT_ALL_INCIDENTS_AND_VIEW_CONFIDENTIAL_NOTES:
    "BehaviourDashboardV2ForProgramme:CanEditAllIncidentsAndViewConfidentialNotes",

  CAN_EDIT_DELETE_INCIDENTS_AND_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS:
    "BehaviourDashboardV2ForProgramme:CanEditDeleteIncidentsAndConfidentialNotesForTheirStudents",
  CAN_EDIT_DELETE_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES:
    "BehaviourDashboardV2ForProgramme:CanEditDeleteAllIncidentsAndConfidentialNotes",
};

export const BEHAVIOUR_MODULE_MODULE_SETTINGS_LIST_RNP = [
  BEHAVIOUR_RNP_PERMISSION.MODULE_SETTING,
  BEHAVIOUR_RNP_FOR_ACCOUNT_CP.MODULE_SETTING,
  BEHAVIOUR_RNP_FOR_PROGRAMME_CP.MODULE_SETTING,
];

export const PERMISSION_ACCESS_MAP = {
  [BEHAVIOUR_RNP_PERMISSION.CAN_VIEW_INCIDENT]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_PERMISSION.CAN_EDIT_INCIDENT]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_PERMISSION.CAN_VIEW_INCIDENT_AND_CONFIDENTIAL]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_PERMISSION.CAN_EDIT_INCIDENT_AND_VIEW_CONFIDENTIAL]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_PERMISSION.CAN_EDIT_INCIDENT_AND_CONFIDENTIAL]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
  },
};

// Permission access map for new behaviour rnp control parameters
export const PERMISSION_ACCESS_MAP_V2 = {
  [BEHAVIOUR_RNP_FOR_ACCOUNT_CP.CAN_VIEW_ALL_INCIDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_VIEW_ALL_INCIDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_VIEW_INCIDENTS_FOR_THEIR_STUDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_FOR_ACCOUNT_CP.CAN_EDIT_ALL_INCIDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_ALL_INCIDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_INCIDENTS_FOR_THEIR_STUDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
  },
  [BEHAVIOUR_RNP_FOR_ACCOUNT_CP.CAN_VIEW_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_VIEW_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_VIEW_INCIDENTS_AND_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_FOR_ACCOUNT_CP.CAN_EDIT_ALL_INCIDENTS_AND_VIEW_CONFIDENTIAL_NOTES]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_ALL_INCIDENTS_AND_VIEW_CONFIDENTIAL_NOTES]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_INCIDENTS_AND_VIEW_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_VIEW,
  },
  [BEHAVIOUR_RNP_FOR_ACCOUNT_CP.CAN_EDIT_DELETE_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_DELETE_ALL_INCIDENTS_AND_CONFIDENTIAL_NOTES]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
  },
  [BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_DELETE_INCIDENTS_AND_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS]: {
    [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
    [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]:
      ACCESSIBILITY_ACCESS_TYPE.CAN_EDIT,
  },
};

export const USER_ASSOCIATED_PROGRAMME_LEVEL_PERMISSIONS = [
  BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_VIEW_INCIDENTS_FOR_THEIR_STUDENTS,
  BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_INCIDENTS_FOR_THEIR_STUDENTS,
  BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_VIEW_INCIDENTS_AND_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS,
  BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_INCIDENTS_AND_VIEW_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS,
  BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_EDIT_DELETE_INCIDENTS_AND_CONFIDENTIAL_NOTES_FOR_THEIR_STUDENTS,
];

export const getPermissionAccessMap = () => {
  const isBehaviourRnPModuleConsumptionEnabled = getIsBehaviourRnPModuleConsumptionEnabled();

  return isBehaviourRnPModuleConsumptionEnabled
    ? PERMISSION_ACCESS_MAP_V2
    : PERMISSION_ACCESS_MAP;
};

const _getBehaviourRnPPermissionList = (
  isBehaviourRnPModuleConsumptionEnabled = false
) => {
  if (!isBehaviourRnPModuleConsumptionEnabled) {
    return _.values(BEHAVIOUR_RNP_PERMISSION);
  }

  return [
    ..._.values(BEHAVIOUR_RNP_FOR_ACCOUNT_CP),
    ..._.values(BEHAVIOUR_RNP_FOR_PROGRAMME_CP),
  ];
};

export const getBehaviourRnPPermissionListMemoized = _.memoize(
  _getBehaviourRnPPermissionList,
  isBehaviourRnPModuleConsumptionEnabled =>
    isBehaviourRnPModuleConsumptionEnabled
);

export const getStateIcon = state => {
  switch (state) {
    case INCIDENT_STATE.DRAFT: {
      return <PlaceholderdotsOutlined variant="warning" />;
    }

    case INCIDENT_STATE.PUBLISHED: {
      return <ClipboardArrowOutlined variant="default" />;
    }

    case INCIDENT_STATE.RESOLVED: {
      return <AssessmentApplicableOutlined variant="success" />;
    }
  }
};

export const COLUMNS_MAPPING = {
  category: "CATEGORY",
  title: "TITLE",
  severity: "LEVEL",
  location: "LOCATION",
  sharedWith: "SHARED_WITH",
  actionTaken: "ACTIONS",
  updatedAt: "UPDATED_AT",
  createdBy: "CREATED_BY",
  createdAt: "CREATED_AT",
  linkedIncidents: "LINKED_INCIDENTS",
  incidentTime: "INCIDENT_TIME",
  updatedBy: "UPDATED_BY",
  description: "DESCRIPTION",
};

export const INCIDENT_FEED_COLUMN = {
  UID: "uid",
  STATUS: "status",
  TITLE: "title",
  CATEGORY: "category",
  SUB_CATEGORY: "sub_category",
  SEVERITY: "severity",
  STUDENT_INVOLVED: "studentInvolved",
  LOCATION: "location",
  CREATED_BY: "createdBy",
  CREATED_AT: "createdAt",
  SHARED_WITH: "sharedWith",
  ACTION_TAKEN: "actionTaken",
  ACTIONS: "action",
  LAST_MODIFIED_AT: "updatedAt",
  INCIDENT_TIME: "incidentTime",
};

export const INCIDENT_FILTER = {
  SEVERITY: "severities",
  STUDENT_INVOLVED: "involvedStudents",
  LOCATION: "locations",
  CREATED_BY: "createdBy",
  SHARED_WITH: "sharedWith",
  REFEREE: "referredTo",
  GRADE: "grades",
  ACTION_TAKEN: "actions",
  CLASS: "classes",
};

export const INCIDENT_FEED_VISIBILITY = {
  CREATED_BY_ME: "CREATED_BY_ME",
  REFERRED_TO_ME: "REFERRED_TO_ME",
  SHARED_WITH_ME: "SHARED_WITH_ME",
};

export const DASHBOARD_SECTION = {
  ALL_INCIDENTS: {
    key: "all",
    label: "behaviour:all_incidents",
    Icon: SinglePointRubricOutlined,
    visibilityFilter: [],
    shortcutKey: {
      code: "Digit1",
      label: "1",
    },
  },
  CREATED_BY_ME: {
    key: "created",
    label: "behaviour:created_by_me",
    Icon: ClipboardPencilLinesOutlined,
    visibilityFilter: [INCIDENT_FEED_VISIBILITY.CREATED_BY_ME],
    shortcutKey: {
      code: "Digit3",
      label: "3",
    },
  },
  REFERRED_TO_ME: {
    key: "referred",
    label: "behaviour:referred_to_me",
    Icon: UserArrowOutlined,
    visibilityFilter: [INCIDENT_FEED_VISIBILITY.REFERRED_TO_ME],
    shortcutKey: {
      code: "Digit2",
      label: "2",
    },
  },
  SHARED_WITH_ME: {
    key: "shared",
    label: "behaviour:shared_with_me",
    Icon: TeacherShareOutlined,
    visibilityFilter: [INCIDENT_FEED_VISIBILITY.SHARED_WITH_ME],
    shortcutKey: {
      code: "Digit4",
      label: "4",
    },
  },
};

const FOOTER_KEY_MAP = {
  SETTINGS: "settings",
  CATEGORIES: "categories",
  SEVERITIES: "severities",
  ACTIONS: "actions",
  LOCATION: "locations",
  DEFAULT_OUTCOMES: "default-outcomes",
  NOMENCLATURE: "nomenclature",
};

export const getBehaviourConfigurationOptions = ({
  t,
  nomenclatureConstants,
  type = "sidebar",
}) => {
  const isFooter = type === "footer";

  const rootKey = isFooter ? FOOTER_KEY_MAP.SETTINGS : "root";
  const rootItem = {
    key: "root",
    children: isFooter
      ? [FOOTER_KEY_MAP.SETTINGS]
      : [
          FOOTER_KEY_MAP.CATEGORIES,
          FOOTER_KEY_MAP.SEVERITIES,
          FOOTER_KEY_MAP.ACTIONS,
          FOOTER_KEY_MAP.LOCATION,
          FOOTER_KEY_MAP.DEFAULT_OUTCOMES,
          FOOTER_KEY_MAP.NOMENCLATURE,
        ],
  };

  const settingsItem = isFooter && {
    key: FOOTER_KEY_MAP.SETTINGS,
    title: t("common:settings"),
    prefix: <SettingsOutlined size={"xx-small"} variant={"subtle"} />,
    isCascadingMenu: true,
    parent: "root",
    tier: 1,
    children: [
      FOOTER_KEY_MAP.CATEGORIES,
      FOOTER_KEY_MAP.SEVERITIES,
      FOOTER_KEY_MAP.ACTIONS,
      FOOTER_KEY_MAP.LOCATION,
      FOOTER_KEY_MAP.DEFAULT_OUTCOMES,
      FOOTER_KEY_MAP.NOMENCLATURE,
    ],
  };

  const options = [
    rootItem,
    ...(isFooter ? [settingsItem] : []),
    {
      key: FOOTER_KEY_MAP.CATEGORIES,
      title: t("common:category_plural"),
      prefix: <GridOutlined size={"xx-small"} variant={"subtle"} />,
      parent: rootKey,
      ...(isFooter && { isCascadingMenu: false, tier: 1 }),
    },
    {
      key: FOOTER_KEY_MAP.SEVERITIES,
      title:
        t("behaviour:plural_label", {
          label: nomenclatureConstants?.LEVEL,
        }) || t("common:levels"),
      prefix: <InsightsOutlined size={"xx-small"} variant={"subtle"} />,
      parent: rootKey,
      ...(isFooter && { isCascadingMenu: false, tier: 1 }),
    },
    {
      key: FOOTER_KEY_MAP.ACTIONS,
      title:
        t("behaviour:plural_label", {
          label: nomenclatureConstants?.ACTION,
        }) || t("common:action_plural"),
      prefix: <TickCircleOpenOutlined size={"xx-small"} variant={"subtle"} />,
      parent: rootKey,
      ...(isFooter && { isCascadingMenu: false, tier: 1 }),
    },
    {
      key: FOOTER_KEY_MAP.LOCATION,
      title: t("common:location_plural"),
      prefix: <PinOutlined size={"xx-small"} variant={"subtle"} />,
      parent: rootKey,
      ...(isFooter && { isCascadingMenu: false, tier: 1 }),
    },
    {
      key: FOOTER_KEY_MAP.DEFAULT_OUTCOMES,
      title: t("behaviour:default_outcomes"),
      prefix: <DashboardOutlined size={"xx-small"} variant={"subtle"} />,
      parent: rootKey,
      ...(isFooter && { isCascadingMenu: false, tier: 1 }),
    },
    {
      key: FOOTER_KEY_MAP.NOMENCLATURE,
      title: t("common:nomenclature"),
      prefix: <LanguageOutlined size={"xx-small"} variant={"subtle"} />,
      parent: rootKey,
      ...(isFooter && { isCascadingMenu: false, tier: 1 }),
    },
  ];

  return options;
};

export const getIsBehaviourSetupCardEnabled = () =>
  ACLStore.can(ENABLE_BEHAVIOUR_SETUP_CARD);

export const getIsNotificationToggleEnabled = () => {
  return ACLStore.can(ENABLE_INCIDENT_NOTIFICATION_TOGGLE);
};

export const getIsRnpSharingInDefaultOutcomesEnabled = () => {
  return ACLStore.can("Behaviour:RnPRoleSharingInDefaultOutcomes");
};

export const getShareWithFieldLabel = ({ t }) => {
  if (getIsNotificationToggleEnabled()) {
    return t("common:visible_to");
  }

  return t("behaviour:shared_with_sentence_case");
};

export const getShareWithEntityLabels = ({ t, isRnPEnabled }) => {
  const entities = [t("common:staff")];

  const studentPerms =
    ACCESSIBILITY_TYPE_PERMISSION_MAP[ACCESSIBILITY_TYPE.INVOLVED_STUDENT];
  if (_.isEmpty(studentPerms) || ACLStore.can(studentPerms)) {
    entities.push(t("common:student"));
  }

  const familyPerms =
    ACCESSIBILITY_TYPE_PERMISSION_MAP[ACCESSIBILITY_TYPE.FAMILY_MEMBERS];
  if (_.isEmpty(familyPerms) || ACLStore.can(familyPerms)) {
    entities.push(t("common:family_member_plural"));
  }

  if (isRnPEnabled) {
    entities.push(t("common:role_plural"));
  }

  if (entities.length === 1) {
    return entities[0];
  }

  return t("common:join_array_with_or", { array: entities });
};

export const getEntityDropdownLabel = ({
  t,
  nomenclatureConstants,
  isRnPEnabled,
  isNotificationToggleEnabled,
}) => {
  const entities = getShareWithEntityLabels({ t, isRnPEnabled });
  const localeParams = {
    entities,
    incident: nomenclatureConstants.INCIDENT,
  };

  if (isNotificationToggleEnabled) {
    return t("behaviour:add_entities_to_incident_visibility", localeParams);
  }

  return t("behaviour:add_entities_to_share_incident", localeParams);
};

export const INCIDENT_ACCESS_LEVEL_MAP = {
  NO_ACCESS: 0,
  CAN_VIEW: 1,
  CAN_EDIT: 2,
  REFERRED: 3,
  OWNER: 3,
};

export const getDashboardNavigationLabel = ({
  t,
  navigationOption,
  nomenclatureConstants,
}) => {
  if (!navigationOption) {
    return "";
  }

  if (navigationOption.key === "all") {
    return t("behaviour:all_incidents", {
      incident: nomenclatureConstants.INCIDENT,
    });
  }

  if (navigationOption.key === "shared" && getIsNotificationToggleEnabled()) {
    return t("behaviour:visible_to_me");
  }

  return t(navigationOption.label);
};

export const BEHAVIOUR_FILTER_TYPE = {
  IS: "IS",
  IS_NOT: "IS_NOT",
};

export const INCIDENT_DATE_FORMAT = "Do MMM YYYY, h:mm A";

export const ACCESSIBILITY_TYPE_LOCALE_MAP = {
  ADMINS: "behaviour:school_administrator_plural",
  INVOLVED_STUDENT: "common:student",
  FAMILY_MEMBERS: "common:family_member_plural",
  HOMEROOM_ADVISOR: "common:homeroom",
  ALL_STAFF: "common:all_staff",
  STAFF: "common:staff_members_plural",
};

export const getCategoryIcon = ({ category, size = "xxx-small" }) => {
  if (!category) {
    return null;
  }

  const color = _.get(category, "color");

  return <SquareFilled style={{ color }} size={size} />;
};

export const getSeverityTagColor = ({ severity }) => {
  const colorMap = {
    ["#B0B0B0"]: "neutral",
    ["#47CEAC"]: "teal",
    ["#26C7D9"]: "blue",
    ["#8888F2"]: "violet",
    ["#FFBB33"]: "yellow",
    ["#FF8DA6"]: "pink",
    ["#FF8D38"]: "orange",
    ["#A9D844"]: "green",
    ["#C88AE8"]: "purple",
    // the below colors were used earlier, need to be removed once data is cleaned
    ["#F5BE52"]: "yellow",
    ["#89DBEC"]: "violet",
    ["#F192A7"]: "pink",
    ["#8888EB"]: "pink",
  };

  return _.get(colorMap, severity?.color, "yellow");
};

export const getIncidentColumnOptions = ({
  t,
  columnConfig,
  nomenclatureConstants,
}) => {
  const columnList = getIncidentFeedColumnList({
    t,
    columnConfig,
    nomenclatureConstants,
  });

  return _.map(columnList, column => _.pick(column, ["id", "label"]));
};

export const getFilterColumnOptions = ({
  t,
  dataFilters,
  userType,
  nomenclatureConstants,
  screen,
}) => {
  let optionConfig =
    userType === "staff"
      ? _.values(INCIDENT_FILTER)
      : [INCIDENT_FILTER.ACTION_TAKEN, INCIDENT_FILTER.SEVERITY];

  if (screen === "STUDENT_PROFILE_RECORD") {
    optionConfig = _.filter(
      optionConfig,
      option => option !== INCIDENT_FILTER.STUDENT_INVOLVED
    );
  }

  const options = [
    {
      value: INCIDENT_FILTER.SEVERITY,
      label: nomenclatureConstants.LEVEL,
    },
    {
      value: INCIDENT_FILTER.LOCATION,
      label: t("common:location"),
    },
    {
      value: INCIDENT_FILTER.STUDENT_INVOLVED,
      label: t("behaviour:student_involved"),
    },
    {
      value: INCIDENT_FILTER.CREATED_BY,
      label: t("common:created_by"),
    },
    {
      value: INCIDENT_FILTER.SHARED_WITH,
      label: getShareWithFieldLabel({ t }),
    },
    {
      value: INCIDENT_FILTER.REFEREE,
      label: t("behaviour:referee"),
    },
    {
      value: INCIDENT_FILTER.GRADE,
      label: t("common:grade"),
    },
    {
      value: INCIDENT_FILTER.ACTION_TAKEN,
      label: t("behaviour:actions_taken", {
        action: nomenclatureConstants.ACTION,
      }),
    },
    {
      value: INCIDENT_FILTER.CLASS,
      label: t("common:class"),
      perms: ["Behaviour:EnableClassFilter"],
    },
  ];

  const filteredOptions = _.filter(options, ({ value, perms }) => {
    if (!_.includes(optionConfig, value)) {
      return false;
    }

    return _.isEmpty(perms) || ACLStore.can(perms);
  });

  return _.map(filteredOptions, option => {
    const isOptionAlreadySelected = _.find(
      dataFilters,
      filter => filter.column?.value === option.value
    );

    return {
      ...option,
      isDisabled: !!isOptionAlreadySelected,
    };
  });
};

export const getDashboardAccessPermissions = ({ loginActiveTab }) => {
  if (loginActiveTab === "admin") {
    return [
      ENABLE_BEHAVIOUR_MODULE_PERM,
      ENABLE_BEHAVIOUR_MODULE_IN_ADMIN_PORTAL,
    ];
  }

  return [ENABLE_BEHAVIOUR_MODULE_PERM];
};

const getBehaviourAccessBlockerProps = ({
  loginActiveTab,
  hasModuleAdminAccess,
  userType,
  curriculumProgramId,
  rnpAccessCheckFn,
  isCombinedCurriculum,
}) => {
  if (isCombinedCurriculum) {
    return {
      isAccessBlocked: !checkIsHomePageFeatureEnabled(
        HOME_PAGE_FEATURE_BEHAVIOR_MANAGEMENT
      ),
    };
  }

  const isAccessBlocked =
    loginActiveTab === "admin" ? !hasModuleAdminAccess : false;

  const isRnPEnabledInBehaviour = ACLStore.can(
    [
      ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
      ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
    ],
    "all"
  );

  if (!isRnPEnabledInBehaviour) {
    return {
      isAccessBlocked,
      moduleId: MODULES.BEHAVIOUR_MANAGEMENT.id,
      customModuleRule: {
        rule: RULE_OPTIONS.lt,
        value: ACCESS_LEVELS.TEACHER_ACCESS.id,
        defaultAccess: false,
      },
    };
  }

  const isBehaviourRnPModuleConsumptionEnabled = getIsBehaviourRnPModuleConsumptionEnabled();

  if (isBehaviourRnPModuleConsumptionEnabled && userType === STAFF) {
    const hasAccess = rnpAccessCheckFn(curriculumProgramId);

    return {
      isAccessBlocked: !hasAccess || isAccessBlocked,
    };
  }

  return {
    isAccessBlocked,
    controlParameterObj: {
      type: "MODULE_SETTINGS",
      controlParameter: BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,
    },
  };
};

export const getBehaviourDashboardRouteAccessBlockerProps = props => {
  return getBehaviourAccessBlockerProps({
    ...props,
    rnpAccessCheckFn: curriculumProgramId =>
      getCanAccessBehaviourDashboardInRnPOrg({ curriculumProgramId }),
  });
};

export const getBehaviourIncidentPageAccessBlockerProps = props => {
  return getBehaviourAccessBlockerProps({
    ...props,
    rnpAccessCheckFn: curriculumProgramId =>
      getIsBehaviourModuleEnabledInModuleSettingsAndRnP(curriculumProgramId),
  });
};

export const getHasModuleAdminAccess = state => {
  const isAdminByRole = getIsAdminWithFallback();
  const userCurriculumPrograms = getUserCurriculumPrograms(state);

  let curriculumIdsWithAdminAccess = [];

  if (!ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    curriculumIdsWithAdminAccess = getAdminPanelModuleAccessCurriculumIds({
      curriculumIds: _.map(userCurriculumPrograms, "id"),
      module: MODULES.BEHAVIOUR_MANAGEMENT.id,
    });
  }

  return isAdminByRole || !_.isEmpty(curriculumIdsWithAdminAccess);
};

export const changeSection = ({
  id,
  type = "replace",
  appendRoute = false,
}) => {
  return dispatch => {
    dispatch(
      goToRelativeRoute({
        route: appendRoute ? id : `../${id}`,
        type,
      })
    );
  };
};

export const FILTER_OPTIONS_LIST = {
  SEARCH_INCIDENT: "SEARCH_INCIDENT",
  RANGE_PICKER: "RANGE_PICKER",
  STATUS: "STATUS",
  CATEGORIES: "CATEGORIES",
  MORE_FILTERS: "MORE_FILTERS",
  CONFIGURE_COLUMNS: "CONFIGURE_COLUMNS",
  DOWNLOAD: "DOWNLOAD",
};

export const FILTER_OPTIONS_LIST_FEATURE_FLAG_MAP = {
  [FILTER_OPTIONS_LIST.RANGE_PICKER]: ENABLE_BEHAVIOUR_DATE_RANGE_FILTER,
};

export const getIsRangePickerEnabled = () => {
  return ACLStore.can(ENABLE_BEHAVIOUR_DATE_RANGE_FILTER);
};

export const getAccessibilityMapMetadata = ({ accessibilityMap }) => {
  const usersWithNoAccess = _.reduce(
    accessibilityMap,
    (userWithNoAccess, accessibility) => {
      const isStaffAccessibility =
        accessibility.type === ACCESSIBILITY_TYPE.STAFF;

      const isNoAccessAccessibility = _.some(accessibility.details, {
        type: ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL,
        value: ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS,
      });

      if (isStaffAccessibility && isNoAccessAccessibility) {
        return [...userWithNoAccess, ...(accessibility.users ?? [])];
      }

      return userWithNoAccess;
    },
    []
  );

  const allStaffAccessibility = _.find(accessibilityMap, {
    type: ACCESSIBILITY_TYPE.ALL_STAFF,
  });
  const adminAccessibility = _.find(accessibilityMap, {
    type: ACCESSIBILITY_TYPE.ADMINS,
  });

  const allStaffHasAccess = _.some(
    allStaffAccessibility?.details,
    detail => detail.value !== ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS
  );
  const adminHasAccess = _.some(
    adminAccessibility?.details,
    detail => detail.value !== ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS
  );

  const ownerAccessibility = _.find(accessibilityMap, accessibility =>
    _.some(accessibility.details, { value: ACCESSIBILITY_ACCESS_TYPE.OWNER })
  );

  const owner = _.head(ownerAccessibility?.users);

  return {
    usersWithNoAccess,
    allStaffHasAccess,
    adminHasAccess,
    owner,
  };
};

export const getSeparatedAccessibilityMap = ({
  adminData,
  accessibilityMap,
  isRnPEnabledInBehaviour,
  curriculumBehaviourAdmins,
  includeBehaviourAdminAccessibility = false,
}) => {
  // Role wise accessibility separation is not required when RnP integration is enabled
  if (isRnPEnabledInBehaviour) {
    return accessibilityMap;
  }

  let combinedStaffAccessibility = null;

  const filteredAccessibilityMap = _.filter(accessibilityMap, accessibility => {
    if (_.includes(["STAFF", "ALL_STAFF"], accessibility.type)) {
      combinedStaffAccessibility = accessibility;
      return false;
    }

    return true;
  });

  const [admins, staffMembers] = _.partition(
    combinedStaffAccessibility?.users,
    user => getIsAdminWithFallback({ user, adminData })
  );

  const behaviourAdminIds = _.get(curriculumBehaviourAdmins, "staffIds", []);

  const [
    behaviourAdminUsers,
    nonBehaviourAdminUsers,
  ] = _.partition(staffMembers, ({ id }) => _.includes(behaviourAdminIds, id));

  const adminAccessibility = {
    type: "ADMINS",
    users: admins,
  };

  const staffAccessibility = {
    type: "STAFF",
    users: includeBehaviourAdminAccessibility
      ? nonBehaviourAdminUsers
      : staffMembers,
  };

  const behaviourAdminAccessibility = {
    type: ACCESSIBILITY_TYPE.BEHAVIOUR_ADMINS,
    users: behaviourAdminUsers,
  };

  if (combinedStaffAccessibility) {
    filteredAccessibilityMap.push(adminAccessibility);
    filteredAccessibilityMap.push(staffAccessibility);

    if (includeBehaviourAdminAccessibility) {
      filteredAccessibilityMap.push(behaviourAdminAccessibility);
    }
  }

  return sortAccessibilityMap({ accessibilityMap: filteredAccessibilityMap });
};

export const getAccessibilityMapWithCombinedStaff = ({
  adminData,
  accessibilityMap,
  curriculumBehaviourAdmins,
  homeroomAdvisorDetails,
}) => {
  const isMultipleHomeroomAdvisorsEnabled = getIsMultipleHomeroomAdvisorsEnabled();

  const ALL_STAFF = {
    users: [],
    hasAccess: false,
    accessibility: null,
  };

  const ADMINS = {
    users: [],
    hasAccess: false,
    accessibility: null,
  };

  const STAFF_WITH_ACCESS = {
    users: [],
    hasAccess: true,
  };

  const STAFF_WITH_NO_ACCESS = {
    users: [],
    hasAccess: false,
  };

  const HOMEROOM_ADVISOR = {
    ...(isMultipleHomeroomAdvisorsEnabled ? { users: [] } : { user: null }),
    hasAccess: false,
    accessibility: null,
  };

  const BEHAVIOUR_ADMINS = {
    users: [],
    hasAccess: false,
    accessibility: null,
  };

  const updatedAccessibilityMap = [];

  _.forEach(accessibilityMap, accessibility => {
    switch (accessibility.type) {
      case "ALL_STAFF": {
        ALL_STAFF.users = accessibility.users;
        ALL_STAFF.hasAccess = _.some(
          accessibility.details,
          detail => detail.value !== "NO_ACCESS"
        );
        ALL_STAFF.accessibility = accessibility;
        break;
      }

      case "ADMINS": {
        ADMINS.users = accessibility.users;
        ADMINS.hasAccess = _.some(
          accessibility.details,
          detail => detail.value !== "NO_ACCESS"
        );
        ADMINS.accessibility = accessibility;
        break;
      }

      case "STAFF": {
        if (
          _.some(accessibility.details, {
            type: "INCIDENT_DETAIL",
            value: "NO_ACCESS",
          })
        ) {
          STAFF_WITH_NO_ACCESS.users.push(...accessibility.users);
          break;
        }

        const incidentDetails = _.find(accessibility.details, {
          type: "INCIDENT_DETAIL",
        });
        const incidentDetailsAccess = _.get(incidentDetails, "value");

        let users = accessibility.users;

        if (incidentDetailsAccess === ACCESSIBILITY_ACCESS_TYPE.OWNER) {
          break;
        }

        if (incidentDetailsAccess === ACCESSIBILITY_ACCESS_TYPE.REFERRED) {
          users = _.map(users, user => ({ ...user, isReferred: true }));
        }

        STAFF_WITH_ACCESS.users.push(...users);

        break;
      }

      case "HOMEROOM_ADVISOR": {
        const staffRelationEdges = _.get(
          homeroomAdvisorDetails,
          "staffRelation.edges",
          []
        );
        const homeroomAdvisorStaffs = !_.isEmpty(homeroomAdvisorDetails)
          ? _.map(staffRelationEdges, "node")
          : null;

        if (isMultipleHomeroomAdvisorsEnabled) {
          HOMEROOM_ADVISOR.users = homeroomAdvisorStaffs ?? accessibility.users;
        } else {
          HOMEROOM_ADVISOR.user =
            _.head(homeroomAdvisorStaffs) ?? _.head(accessibility.users);
        }
        HOMEROOM_ADVISOR.hasAccess = _.some(
          accessibility.details,
          detail => detail.value !== "NO_ACCESS"
        );
        HOMEROOM_ADVISOR.accessibility = accessibility;
        break;
      }

      case ACCESSIBILITY_TYPE.BEHAVIOUR_ADMINS: {
        BEHAVIOUR_ADMINS.users = accessibility.users;
        BEHAVIOUR_ADMINS.accessibility = accessibility;
        BEHAVIOUR_ADMINS.hasAccess = _.some(
          accessibility.details,
          detail => detail.value !== "NO_ACCESS"
        );
        break;
      }

      default: {
        if (
          _.some(accessibility.details, detail => detail.value !== "NO_ACCESS")
        ) {
          updatedAccessibilityMap.push(accessibility);
        }
      }
    }
  });

  const includeAllStaff =
    ALL_STAFF.hasAccess && _.isEmpty(STAFF_WITH_NO_ACCESS.users);

  const combinedStaffList = STAFF_WITH_ACCESS.users;

  const filteredStaffList = _.filter(
    combinedStaffList,
    staff => !_.some(STAFF_WITH_NO_ACCESS.users, { id: staff.id })
  );

  if (isMultipleHomeroomAdvisorsEnabled) {
    const homeroomAdvisorStaffList = _.intersectionBy(
      filteredStaffList,
      HOMEROOM_ADVISOR.users,
      "id"
    );

    if (HOMEROOM_ADVISOR.hasAccess || !_.isEmpty(homeroomAdvisorStaffList)) {
      const homeroomAdvisors = _.map(HOMEROOM_ADVISOR.users, user => {
        return _.find(filteredStaffList, { id: user?.id }) ?? user;
      });

      updatedAccessibilityMap.push({
        ...HOMEROOM_ADVISOR.accessibility,
        users: _.compact(homeroomAdvisors),
      });
    }
  } else {
    const homeroomAdvisorStaff = _.find(filteredStaffList, {
      id: HOMEROOM_ADVISOR.user?.id,
    });

    if (HOMEROOM_ADVISOR.hasAccess || homeroomAdvisorStaff) {
      const homeroomAdvisor = homeroomAdvisorStaff ?? HOMEROOM_ADVISOR.user;

      updatedAccessibilityMap.push({
        ...HOMEROOM_ADVISOR.accessibility,
        users: homeroomAdvisor ? [homeroomAdvisor] : [],
      });
    }
  }

  const adminsWithNoAccess = _.filter(STAFF_WITH_NO_ACCESS.users, user =>
    getIsAdminWithFallback({ user, adminData })
  );

  // When `All admins` grouped entity have access and all individual admins have `No access`
  if (_.size(adminsWithNoAccess) === adminData?.totalCount) {
    ADMINS.hasAccess = false;
  }

  const includeBehaviourAdminAccessibility =
    BEHAVIOUR_ADMINS.hasAccess &&
    !_.isEmpty(curriculumBehaviourAdmins?.staffIds);

  if (
    !_.isEmpty(filteredStaffList) ||
    ALL_STAFF.hasAccess ||
    ADMINS.hasAccess ||
    includeBehaviourAdminAccessibility
  ) {
    updatedAccessibilityMap.push({
      id: "INCIDENT_STAFF",
      type: includeAllStaff ? "ALL_STAFF" : "STAFF",
      users: filteredStaffList,
    });
  }

  return sortAccessibilityMap({ accessibilityMap: updatedAccessibilityMap });
};

export const getGroupedStaffCount = ({
  adminData,
  accessibilityMap,
  totalAdminCount,
  totalAllStaffCount,
  curriculumBehaviourAdmins,
}) => {
  const allStaffAccessibility = _.find(accessibilityMap, {
    type: ACCESSIBILITY_TYPE.ALL_STAFF,
  });
  const allStaffAccess = _.find(allStaffAccessibility?.details, {
    type: ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL,
  });

  const adminAccessibility = _.find(accessibilityMap, {
    type: ACCESSIBILITY_TYPE.ADMINS,
  });
  const adminAccess = _.find(adminAccessibility?.details, {
    type: ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL,
  });

  const staffAccessibilityMap = _.filter(accessibilityMap, {
    type: ACCESSIBILITY_TYPE.STAFF,
  });

  const hasBehaviourAdminAccessibility = _.some(accessibilityMap, {
    type: ACCESSIBILITY_TYPE.BEHAVIOUR_ADMINS,
  });

  const behaviourAdminIds = _.get(curriculumBehaviourAdmins, "staffIds", []);

  const canAllStaffAccess =
    allStaffAccess &&
    allStaffAccess.value !== ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS;
  const canAdminAccess =
    adminAccess && adminAccess.value !== ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS;

  let totalStaffCount = 0;
  const staffWithAccess = new Set();

  if (canAllStaffAccess) {
    totalStaffCount = totalAllStaffCount;
  } else if (canAdminAccess) {
    totalStaffCount = totalAdminCount;
  }

  _.forEach(staffAccessibilityMap, staffAccessibility => {
    const user = _.head(staffAccessibility.users);
    const { value } = _.find(staffAccessibility.details, {
      type: ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL,
    });

    const isOwner = value === ACCESSIBILITY_ACCESS_TYPE.OWNER;
    const isAdminUser = user && getIsAdminWithFallback({ user, adminData });

    if (value === ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS) {
      if (!canAllStaffAccess && !isAdminUser) {
        return;
      }

      if (canAllStaffAccess || canAdminAccess) {
        totalStaffCount -= 1;
      }
    } else {
      if (
        isOwner ||
        canAllStaffAccess ||
        (isAdminUser && canAdminAccess) ||
        staffWithAccess.has(user?.id) ||
        (hasBehaviourAdminAccessibility &&
          _.includes(behaviourAdminIds, user?.id))
      ) {
        return;
      }

      if (user?.id) {
        staffWithAccess.add(user?.id);
      }

      totalStaffCount += 1;
    }
  });

  if (hasBehaviourAdminAccessibility && !_.isEmpty(behaviourAdminIds)) {
    totalStaffCount += _.size(behaviourAdminIds);
  }

  return totalStaffCount;
};

export const getIncidentFeedColumnList = ({
  t,
  columnConfig,
  showActions = false,
  nomenclatureConstants,
}) => {
  const COLUMN_LIST = [
    {
      id: INCIDENT_FEED_COLUMN.STATUS,
      label: t("common:status"),
      type: "CUSTOM",
      isSticky: true,
    },
    {
      id: INCIDENT_FEED_COLUMN.UID,
      label: t("behaviour:incident_id", {
        incident: nomenclatureConstants.INCIDENT,
      }),
      type: "TEXTCOLUMN",
      style: {
        width: "110px",
      },
      isSticky: true,
    },
    {
      id: INCIDENT_FEED_COLUMN.CATEGORY,
      label: t("common:category"),
      type: "CUSTOM",
      style: {
        width: "192px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.TITLE,
      label: t("behaviour:incident_title", {
        incident: nomenclatureConstants.INCIDENT,
      }),
      type: "TEXTCOLUMN",
      style: {
        width: "416px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.SUB_CATEGORY,
      label: t("behaviour:sub_category"),
      type: "TEXTCOLUMN",
      style: {
        width: "300px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.SEVERITY,
      label: nomenclatureConstants.LEVEL,
      type: "CUSTOM",
      entity: "BEHAVIOUR_SEVERITY",
      style: {
        width: "126px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.STUDENT_INVOLVED,
      label: t("behaviour:student_involved"),
      type: "CUSTOM",
      style: {
        width: "240px",
      },
      viewMode: "NAME_AND_CUSTOM_SUBTEXT",
      customCellStyle: {
        gap: "8px",
      },
      emptyTextLocale: "configuration:-",
      emptyTextStyle: {
        color: colors.textDefault,
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.LOCATION,
      label: t("common:location"),
      type: "TEXTCOLUMN",
      style: {
        width: "184px",
      },
      entity: "BEHAVIOUR_LOCATION",
      sortType: "toggle",
      hasSortingEnabled: true,
    },
    {
      id: INCIDENT_FEED_COLUMN.SHARED_WITH,
      label: getShareWithFieldLabel({ t }),
      type: "CUSTOM",
    },
    {
      id: INCIDENT_FEED_COLUMN.ACTION_TAKEN,
      label: t("behaviour:actions_taken", {
        action: nomenclatureConstants.ACTION,
      }),
      type: "CUSTOM",
      style: {
        width: "148px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.LAST_MODIFIED_AT,
      label: t("common:last_modified_on"),
      type: "TEXTCOLUMN",
      entity: "UPDATED_AT",
      sortType: "toggle",
      hasSortingEnabled: true,
      style: {
        width: "208px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.CREATED_BY,
      label: t("common:created_by"),
      type: "CUSTOM",
      style: {
        width: "300px",
      },
      customCellStyle: {
        gap: "8px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.CREATED_AT,
      label: t("common:created_on"),
      type: "TEXTCOLUMN",
      entity: "CREATED_AT",
      sortType: "toggle",
      hasSortingEnabled: true,
      style: {
        width: "208px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.INCIDENT_TIME,
      label: t("behaviour:incident_time", {
        incident: nomenclatureConstants.INCIDENT,
      }),
      type: "TEXTCOLUMN",
      entity: "BEHAVIOUR_INCIDENT_TIME",
      sortType: "toggle",
      hasSortingEnabled: true,
      perm: ENABLE_BEHAVIOUR_DATE_RANGE_FILTER,
      style: {
        width: "208px",
      },
    },
    {
      id: INCIDENT_FEED_COLUMN.ACTIONS,
      type: "CUSTOM",
      style: {
        width: "36px",
      },
    },
  ];

  return _.filter(COLUMN_LIST, columnOption => {
    if (columnOption.perm && !ACLStore.can(columnOption.perm)) {
      return false;
    }

    if (columnOption.id === INCIDENT_FEED_COLUMN.ACTIONS) {
      return showActions;
    }

    return _.includes(columnConfig, columnOption.id);
  });
};

export const sortAccessibilityMap = ({ accessibilityMap, customOrder }) => {
  const defaultOrder = [
    "ADMINS",
    "HOMEROOM_ADVISOR",
    "STAFF",
    "ALL_STAFF",
    ACCESSIBILITY_TYPE.ROLE,
    "FAMILY_MEMBERS",
    "INVOLVED_STUDENT",
  ];

  return mapOrder({
    array: accessibilityMap,
    order: customOrder ?? defaultOrder,
    key: "type",
  });
};

export const mapOrder = ({ array, order, key }) => {
  return _.sortBy(array, element => _.indexOf(order, _.get(element, key, "")));
};

export const getIsReferralAction = action => {
  const actionKey = _.get(action, "metadata.actionKey");
  return actionKey === REFERRAL_ACTION_KEY;
};

export const getAppliedActionsWithReferral = ({
  t,
  appliedActions,
  accessibilityMap,
}) => {
  const referredUsers = [];

  const updatedAppliedActions = _.filter(
    appliedActions,
    ({ action }) => !getIsReferralAction(action)
  );

  _.forEach(accessibilityMap, accessibility => {
    const incidentDetailsAccess = _.get(accessibility, "details[0].value");
    const isReferredEntity = incidentDetailsAccess === "REFERRED";

    if (isReferredEntity) {
      referredUsers.push({
        type: accessibility.type,
        user: _.first(accessibility.users),
      });
    }
  });

  const referralAction = _.find(appliedActions, ({ action }) =>
    getIsReferralAction(action)
  );

  if (_.isEmpty(referredUsers) || !referralAction) {
    return updatedAppliedActions;
  }

  const formattedReferralActions = _.map(referredUsers, userObj => {
    const referralLabel = getFormattedUserName({ user: userObj.user });

    return {
      ...referralAction,
      id: generateRandomIdV2(),
      action: {
        ...referralAction?.action,
        id: generateRandomIdV2(),
        title: t("behaviour:refer_to_label", { label: referralLabel }),
      },
    };
  });

  updatedAppliedActions.push(...formattedReferralActions);

  return updatedAppliedActions;
};

export const getAcademicYearDurationLabel = ({ startDate, endDate }) => {
  return `${formatDate({
    date: startDate,
    format: "MMM, YYYY",
  })} - ${formatDate({
    date: endDate,
    format: "MMM, YYYY",
  })}`;
};

export const convertDateToISOFormat = (
  date,
  format = DATE_TIME_CONSTANTS.YYYY_MM_DD
) => {
  return getDateMomentObjectCustom(date, format).toISOString();
};

export const getMomentObj = date => {
  return moment(date);
};

export const getMonthName = month => {
  return moment(_.toString(month), "MM").format("MMM");
};

export const getAccessibilityMapUserCount = ({
  accessibilityMap,
  combinedStaffCount,
}) => {
  return _.sumBy(accessibilityMap, accessibility => {
    const isStaffAccessibility = _.includes(
      [ACCESSIBILITY_TYPE.ALL_STAFF, ACCESSIBILITY_TYPE.STAFF],
      accessibility.type
    );

    return isStaffAccessibility
      ? combinedStaffCount
      : _.size(accessibility.users);
  });
};

export const getSheetNameLabel = ({
  visibility,
  t,
  date,
  nomenclatureConstants,
}) => {
  switch (visibility) {
    case INCIDENT_FEED_VISIBILITY.CREATED_BY_ME: {
      return t("behaviour:incidents_with_label_date", {
        label: t("common:created"),
        date,
        incident: nomenclatureConstants.INCIDENT,
      });
    }

    case INCIDENT_FEED_VISIBILITY.REFERRED_TO_ME: {
      return t("behaviour:incidents_with_label_date", {
        label: t("behaviour:referred"),
        date,
        incident: nomenclatureConstants.INCIDENT,
      });
    }

    case INCIDENT_FEED_VISIBILITY.SHARED_WITH_ME: {
      return t("behaviour:incidents_with_label_date", {
        label: t("common:shared"),
        date,
        incident: nomenclatureConstants.INCIDENT,
      });
    }

    default: {
      return t("behaviour:all_incident_with_date", {
        date,
        incident: nomenclatureConstants.INCIDENT,
      });
    }
  }
};

export const isBehaviourIncidentNotificationType = ({ notificationType }) => {
  return _.includes(
    [
      "BEHAVIOUR_INCIDENT_UPDATES",
      "BEHAVIOUR_INCIDENT_ACTIONS",
      "BEHAVIOUR_INCIDENT_ACCESSIBILITY",
    ],
    notificationType
  );
};

export const getIsReferredSuffixLabel = ({ t, user }) => {
  let suffixLabel = "";

  if (user?.isReferred) {
    suffixLabel = ` (${t("behaviour:referred_sentence_case")})`;
  } else if (user?.isOwner) {
    suffixLabel = ` (${t("common:creator")})`;
  }

  return suffixLabel;
};

export const sortByOrder = ({ items = [], order = [], identity = "id" }) => {
  const orderMap = new Map();

  _.forEach(order, (item, index) => {
    orderMap.set(item[identity], index);
  });

  return [...items].sort((a, b) => {
    // If both a and b are in order array, compare their indices
    if (orderMap.has(a[identity]) && orderMap.has(b[identity])) {
      return orderMap.get(a[identity]) - orderMap.get(b[identity]);
    }
    // If only a is in order array, it comes first
    if (orderMap.has(a[identity])) {
      return -1;
    }
    // If only b is in order array, it comes first
    if (orderMap.has(b[identity])) {
      return 1;
    }
    // If neither a nor b are in order array, they remain in their original order
    return 0;
  });
};

// NOTE: This array should contain config for all columns that are available in the incident feed
export const DEFAULT_COLUMN_FILTERS = [
  { id: INCIDENT_FEED_COLUMN.STATUS, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.UID, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.CATEGORY, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.TITLE, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.SUB_CATEGORY, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.SEVERITY, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.STUDENT_INVOLVED, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.LOCATION, isSelected: false },
  { id: INCIDENT_FEED_COLUMN.SHARED_WITH, isSelected: false },
  { id: INCIDENT_FEED_COLUMN.ACTION_TAKEN, isSelected: true },
  { id: INCIDENT_FEED_COLUMN.LAST_MODIFIED_AT, isSelected: false },
  { id: INCIDENT_FEED_COLUMN.CREATED_BY, isSelected: false },
  { id: INCIDENT_FEED_COLUMN.CREATED_AT, isSelected: false },
  { id: INCIDENT_FEED_COLUMN.INCIDENT_TIME, isSelected: false },
];

export const getIsTextMatch = ({ text, pattern }) => {
  const searchRegex = new RegExp(_.escapeRegExp(pattern), "gi");
  return searchRegex.test(text);
};

export const EVENT_NAME = "BEHAVIOUR_MANAGEMENT";

export const EVENT_TYPES = {
  FILTER_MODAL: "FILTER_MODAL",
  INCIDENT_REPORTS_DOWNLOAD: "INCIDENT_REPORTS_DOWNLOAD",
  SHORTCUT_INCIDENTS_DASHBOARD: "SHORTCUT_INCIDENTS_DASHBOARD",
  CONFIGURE_COLUMNS_DASHBOARD: "CONFIGURE_COLUMNS_DASHBOARD",
  ACTIVITY_LOG: "ACTIVITY_LOG",
  ENABLE_TODDLE_DEFINED_ACTIONS: "ENABLE_TODDLE_DEFINED_ACTIONS",
  DISABLE_TODDLE_DEFINED_ACTIONS: "DISABLE_TODDLE_DEFINED_ACTIONS",
};

export const INCIDENT_REPORTS_DOWNLOAD_TYPES = {
  ALL_DATA: "ALL_DATA",
  FILTERED: "FILTERED",
};

export const getIsGlobalAccessAccessibility = ({ accessibility }) => {
  if (accessibility.type === ACCESSIBILITY_TYPE.BEHAVIOUR_ADMINS) {
    return true;
  }

  const settingValue = getSettingValue({
    name: "Behaviour:UsersWithGlobalIncidentAccess",
  });

  const isStaffAccessibilityType =
    accessibility?.type === ACCESSIBILITY_TYPE.STAFF;

  const isGlobalAccessAccessibility = _.some(accessibility?.users, user =>
    _.includes(settingValue, user.id)
  );

  return isStaffAccessibilityType && isGlobalAccessAccessibility;
};

export const checkIsValidShortcut = pressedKey => ({ shortcutKey }) => {
  const {
    code,
    altKey = false,
    shiftKey = false,
    ctrlKey = false,
    metaKey = false,
  } = pressedKey;

  const {
    code: shortcutCode,
    altKey: shortcutAltKey = false,
    shiftKey: shortcutShiftKey = false,
    ctrlKey: shortcutCtrlKey = false,
    metaKey: shortcutMetaKey = false,
  } = shortcutKey ?? {};

  return (
    code === shortcutCode &&
    altKey === shortcutAltKey &&
    shiftKey === shortcutShiftKey &&
    ctrlKey === shortcutCtrlKey &&
    metaKey === shortcutMetaKey
  );
};

export const getHasIncidentAccessByGroupTypes = ({
  groupTypes,
  accessibilityMap,
}) => {
  return _.map(groupTypes, groupType => {
    const groupAccessibility = _.find(accessibilityMap, {
      type: groupType,
    });

    const groupIncidentAccess = _.find(groupAccessibility?.details, {
      type: ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL,
    });

    return (
      groupIncidentAccess &&
      groupIncidentAccess.value !== ACCESSIBILITY_ACCESS_TYPE.NO_ACCESS
    );
  });
};

export const getACLPermissionStaffQueryVariable = () => {
  const state = store.getState();
  const academicYearId = getSelectedAcademicYearId(state);
  const adminPortalPermissionId = ACLStorage.getControlParameterIdByContext(
    ADMIN_PORTAL_ACCESS_PERMISSION,
    "MODULE_SETTINGS",
    []
  );

  return {
    permissionId: adminPortalPermissionId,
    contexts: [],
    academicYearId,
  };
};

/*
 * NOTE: This function should be only used under the context of ACLPermissionStaff query.
 * @param  {Object | undefined} user - User details object to check permission for specific user.
 * @return {boolean} Weather passed staff user or current user is admin or not. (By role or RnP Admin portal access)
 */
export const getIsAdminWithFallback = ({ user, adminData } = {}) => {
  if (!ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    if (user) {
      return user.role === ADMIN;
    }

    return getIsAdminByRole();
  }

  if (user) {
    const rnpAdminList = _.get(adminData, "data", []);

    return !!_.find(rnpAdminList, { id: user.id });
  }

  return ACLStorage.getModuleSettingsValueByContext(
    ADMIN_PORTAL_ACCESS_PERMISSION,
    []
  );
};

export const getCanAccessBehaviourSetup = ({ loginActiveTab }) => {
  const isBehaviourEnabledInRnp = ACLStore.can(
    ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR
  );
  const isAdminLoggedIn = _.includes(
    ["admin", "accountSettingsTab"],
    loginActiveTab
  );
  const hasModuleAdminAccess = getHasModuleAdminAccess();

  if (!ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    return isAdminLoggedIn && hasModuleAdminAccess;
  } else {
    return isBehaviourEnabledInRnp
      ? ACLStorage.getModuleSettingsValueByContext(
          BEHAVIOUR_SETUP_ACCESS_PERMISSION,
          []
        )
      : ACLStorage.getModuleSettingsValueByContext(
          ADMIN_PORTAL_ACCESS_PERMISSION,
          []
        );
  }
};

export const getBehaviourPermissionIds = ({ curriculumId, permissions }) => {
  return _.map(permissions, permission => {
    const isModuleSetting = _.includes(
      BEHAVIOUR_MODULE_MODULE_SETTINGS_LIST_RNP,
      permission
    );

    return ACLStorage.getControlParameterIdByContext(
      permission,
      isModuleSetting ? "MODULE_SETTINGS" : "PERMISSION",
      [
        {
          entityType: "CURRICULUM_PROGRAM",
          entityId: curriculumId,
        },
      ]
    );
  });
};

export const getPermittedStaffMembersVariables = _.memoize(
  ({
    searchText,
    curriculumProgramIds = [],
    academicYearId,
    organizationId,
  }) => {
    if (_.isEmpty(curriculumProgramIds)) {
      return null;
    }

    const controlParameters = [
      BEHAVIOUR_RNP_ACCOUNT_LEVEL_MODULE_SETTING,
      BEHAVIOUR_RNP_PROGRAMME_LEVEL_MODULE_SETTING,
    ];

    const controlParameterIds = getBehaviourPermissionIds({
      permissions: controlParameters,
      curriculumId: _.first(curriculumProgramIds),
    });

    return {
      organizationId,
      input: {
        first: 25,
        searchText,
        curriculumProgramIds,
        academicYearId,
        controlParameterIds,
        fetchBehaviourRolesOnly: true,
      },
    };
  },
  ({
    searchText,
    curriculumProgramIds = [],
    academicYearId,
    organizationId,
  }) => {
    return `${searchText}_${_.join(
      curriculumProgramIds,
      ","
    )}_${academicYearId}_${organizationId}`;
  }
);

export const getACLPermissionStaffRolesVariables = ({
  incident,
  academicYearId,
  permissions,
  organizationId,
  isBehaviourRnPModuleConsumptionEnabled,
}) => {
  const studentCurriculumId = _.get(
    incident,
    "involvedStudent.yearGroup.curriculumProgram.id"
  );

  if (!studentCurriculumId) {
    return null;
  }

  const permissionIds = getBehaviourPermissionIds({
    curriculumId: studentCurriculumId,
    permissions,
  });

  return {
    ids: permissionIds,
    academicYearId,
    assignedUsersFilters: {
      contextFilters: [
        {
          contextId: studentCurriculumId,
          contextType: "CURRICULUM_PROGRAM",
        },
        ...(isBehaviourRnPModuleConsumptionEnabled
          ? [
              {
                contextId: organizationId,
                contextType: "ORGANIZATION",
              },
            ]
          : []),
      ],
    },
  };
};

export const getPermissionRoles = ({
  rnpRoles,
  combinedPermissionData,
  isBehaviourRnPModuleConsumptionEnabled,
}) => {
  const permissionsToCheck = isBehaviourRnPModuleConsumptionEnabled
    ? [
        BEHAVIOUR_RNP_FOR_ACCOUNT_CP.MODULE_SETTING,
        BEHAVIOUR_RNP_FOR_PROGRAMME_CP.MODULE_SETTING,
      ]
    : [BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION];

  return _.reduce(
    rnpRoles,
    (permissionRoles, rnpRole) => {
      const role = _.find(
        _.flatMap(
          permissionsToCheck,
          permissionKey =>
            combinedPermissionData?.[permissionKey]?.enabledRoles || []
        ),
        { id: rnpRole.id }
      );

      if (role) {
        permissionRoles.push({
          ...role,
          contextUserCount: role.assignedUsers?.totalCount || 0,
        });
      }

      return permissionRoles;
    },
    []
  );
};

export const getRoleAccessibilityConfig = ({
  roleId,
  combinedPermissionData,
}) => {
  if (!roleId) {
    return null;
  }

  let accessibilityConfig = null;

  const permissionAccessMap = getPermissionAccessMap();

  const accessibilityPermissions = _.keys(permissionAccessMap);

  _.forEach(accessibilityPermissions, permission => {
    const permissionData = combinedPermissionData?.[permission];

    if (!permissionData?.enabledRoles) {
      return;
    }

    const isPermissionEnabledInRole = _.some(permissionData.enabledRoles, {
      id: roleId,
    });

    if (isPermissionEnabledInRole) {
      const currentAccessibilityConfig = permissionAccessMap[permission];

      if (!accessibilityConfig) {
        accessibilityConfig = currentAccessibilityConfig;
        return;
      }

      const currentIncidentAccess =
        currentAccessibilityConfig[ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL];
      const currentIncidentAccessLevel =
        ACCESSIBILITY_LEVEL_MAP[currentIncidentAccess];

      const currentConfidentialAccess =
        currentAccessibilityConfig[
          ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL
        ];
      const currentConfidentialAccessLevel =
        ACCESSIBILITY_LEVEL_MAP[currentConfidentialAccess];

      const incidentAccess =
        accessibilityConfig[ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL];
      const incidentAccessLevel = ACCESSIBILITY_LEVEL_MAP[incidentAccess];

      const confidentialAccess =
        accessibilityConfig[ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL];
      const confidentialAccessLevel =
        ACCESSIBILITY_LEVEL_MAP[confidentialAccess];

      if (
        currentConfidentialAccessLevel > confidentialAccessLevel ||
        (currentIncidentAccessLevel > incidentAccessLevel &&
          currentConfidentialAccessLevel === confidentialAccessLevel)
      ) {
        accessibilityConfig = currentAccessibilityConfig;
      }
    }
  });

  return accessibilityConfig;
};

const isConfigHigherOrEqual = (configA, configB) => {
  const aIncident =
    ACCESSIBILITY_LEVEL_MAP[configA[ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]];
  const aConfidential =
    ACCESSIBILITY_LEVEL_MAP[
      configA[ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]
    ];

  const bIncident =
    ACCESSIBILITY_LEVEL_MAP[configB[ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]];
  const bConfidential =
    ACCESSIBILITY_LEVEL_MAP[
      configB[ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]
    ];

  if (
    aConfidential > bConfidential ||
    (aConfidential === bConfidential && aIncident >= bIncident)
  ) {
    return true;
  }

  return false;
};

const getHigherAccessibilityConfig = (existingConfig, newConfig) => {
  if (!existingConfig) {
    return newConfig;
  }

  return isConfigHigherOrEqual(existingConfig, newConfig)
    ? existingConfig
    : newConfig;
};

export const getBehaviourRnPRoleAccessibilityConfig = ({
  roleId,
  isAccountLevelRole,
  combinedPermissionData,
  accessibility,
}) => {
  if (isAccountLevelRole) {
    const incidentDetail = _.find(accessibility.details, {
      type: ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL,
    });

    const confidentialDetail = _.find(accessibility.details, {
      type: ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL,
    });

    const incidentDetailAccess = incidentDetail.value;
    const confidentialDetailAccess = confidentialDetail.value;

    return {
      ALL_USERS: {
        [ACCESSIBILITY_DETAIL_TYPE.INCIDENT_DETAIL]: incidentDetailAccess,
        [ACCESSIBILITY_DETAIL_TYPE.CONFIDENTIAL_DETAIL]: confidentialDetailAccess,
      },
    };
  }

  if (!roleId) {
    return null;
  }

  let allAccessConfig = null;
  let theirAccessConfig = null;

  const permissionAccessMap = getPermissionAccessMap();

  const PROGRAMME_LEVEL_PERMISSIONS = Object.values(
    BEHAVIOUR_RNP_FOR_PROGRAMME_CP
  );

  const accessibilityPermissions = _.keys(
    permissionAccessMap
  ).filter(permission => PROGRAMME_LEVEL_PERMISSIONS.includes(permission));

  _.forEach(accessibilityPermissions, permission => {
    const permissionData = combinedPermissionData?.[permission];

    if (!permissionData?.enabledRoles) {
      return;
    }

    const isPermissionEnabledInRole = _.some(permissionData.enabledRoles, {
      id: roleId,
    });

    if (!isPermissionEnabledInRole) {
      return;
    }

    const currentConfig = permissionAccessMap[permission];

    const isTheirPermission = _.includes(
      USER_ASSOCIATED_PROGRAMME_LEVEL_PERMISSIONS,
      permission
    );

    if (isTheirPermission) {
      theirAccessConfig = getHigherAccessibilityConfig(
        theirAccessConfig,
        currentConfig
      );
    } else {
      allAccessConfig = getHigherAccessibilityConfig(
        allAccessConfig,
        currentConfig
      );
    }
  });

  if (!allAccessConfig && !theirAccessConfig) {
    return null;
  }

  // No THEIR permission but ALL exists -> inherit ALL
  if (allAccessConfig && !theirAccessConfig) {
    return {
      ALL_USERS: allAccessConfig,
      ASSOCIATED_USERS: allAccessConfig,
    };
  }

  // If both exist and ALL is higher or equal -> normalize to ALL
  if (
    allAccessConfig &&
    theirAccessConfig &&
    isConfigHigherOrEqual(allAccessConfig, theirAccessConfig)
  ) {
    return {
      ALL_USERS: allAccessConfig,
      ASSOCIATED_USERS: allAccessConfig,
    };
  }

  // Otherwise keep them separate
  return {
    ALL_USERS: allAccessConfig,
    ASSOCIATED_USERS: theirAccessConfig,
  };
};

export const getLanguageConfig = () => {
  const userInfo = getUserInfo();
  const userId = _.get(userInfo, "id");
  const organizationId = getOrganizationId();

  const configurationName = isUserPreferredAIVoiceLanguageModuleWiseEnabled()
    ? "ConfigureRTEAIVoiceLanguage"
    : "ConfigureAIVoiceLanguage";

  const selectedLanguageValue = getSettingValue({
    name: configurationName,
    organizationId,
    userId,
    readFromUserAndOrgLevel: true,
  });

  const language = _.find(AI_TRANSLATION_LANGUAGES, {
    id: _.toNumber(selectedLanguageValue),
  });

  return language;
};

export const getIsCurriculumBoundaryApplicable = () => {
  return ACLStore.can("EnableProgramContextForBehaviour");
};

export const getCurriculumContextMemoized = _.memoize(
  ({ curriculumPrograms = [] }) => {
    return _.map(curriculumPrograms, ({ id }) => {
      return [
        {
          entityId: id,
          entityType: "CURRICULUM_PROGRAM",
        },
      ];
    });
  },
  ({ curriculumPrograms }) => _.map(curriculumPrograms, "id").join(", ")
);

export const getIsDescriptionTemplateEnabled = () => {
  return ACLStore.can(ENABLE_DESCRIPTION_TEMPLATE_IN_BEHAVIOUR);
};

export const getIsBehaviourEnabledInRNP = () => {
  return ACLStore.can(
    [
      ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
      ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
    ],
    "all"
  );
};

export const DATE_FORMAT = "DD MMM YYYY";

export const getCurrentAcademicYearStartDate = () => {
  const organizationId = getOrganizationId();
  return getAcademicYearStartDate({ organizationId });
};

export const getCurrentAcademicYearEndDate = () => {
  const organizationId = getOrganizationId();
  return getAcademicYearEndDate({ organizationId });
};

export const LAST_7_DAYS = "LAST_7_DAYS";
export const LAST_30_DAYS = "LAST_30_DAYS";
export const LAST_90_DAYS = "LAST_90_DAYS";
export const LAST_6_MONTHS = "LAST_6_MONTHS";
export const CURRENT_ACADEMIC_YEAR = "CURRENT_ACADEMIC_YEAR";
export const CUSTOM_DURATION = "CUSTOM_DURATION";
export const PAST_ACADEMIC_YEARS = "PAST_ACADEMIC_YEARS";

const dateRangeDaysMap = {
  [LAST_7_DAYS]: 7,
  [LAST_30_DAYS]: 30,
  [LAST_90_DAYS]: 90,
  [LAST_6_MONTHS]: 180,
};

export const getStartOfDay = ({ date, shouldFormat = true }) => {
  return shouldFormat
    ? moment(date).startOf("day").format()
    : moment(date).startOf("day");
};

export const getEndOfDay = ({ date }) => {
  return moment(date).endOf("day").format();
};

export const isCurrentAcademicYearEndInSameYear = ({ startDate, endDate }) => {
  return _.isEqual(
    formatDate({ date: startDate, format: "YYYY" }),
    formatDate({ date: endDate, format: "YYYY" })
  );
};

const getLabelForAcademicYear = academicYear => {
  const startDate = getStartOfDay({ date: academicYear?.startDate });
  const endDate = getEndOfDay({ date: academicYear?.endDate });

  return isCurrentAcademicYearEndInSameYear({ startDate, endDate })
    ? formatDate({ date: startDate, format: "YYYY" })
    : getAcademicYearLabel({ startDate, endDate });
};

export const getCurrentAcademicYearLabel = () => {
  const organizationId = getOrganizationId();
  const currentAcademicYear = getCurrentAcademicYear({ organizationId });
  return getLabelForAcademicYear(currentAcademicYear);
};

export const getPreviousAcademicYearLabel = () => {
  const state = store.getState();
  const previousAcademicYear = getSelectedAcademicYear(state);
  return getLabelForAcademicYear(previousAcademicYear);
};

export const DATE_RANGE_OPTIONS = [
  {
    key: LAST_7_DAYS,
    value: LAST_7_DAYS,
    label: "common:last_7_days",
  },
  {
    key: LAST_30_DAYS,
    value: LAST_30_DAYS,
    label: "common:last_30_days",
  },
  {
    key: LAST_90_DAYS,
    value: LAST_90_DAYS,
    label: "common:last_90_days",
  },
  {
    key: LAST_6_MONTHS,
    value: LAST_6_MONTHS,
    label: "common:last_6_months",
  },
  {
    key: CURRENT_ACADEMIC_YEAR,
    value: CURRENT_ACADEMIC_YEAR,
    label: "behaviour:current_academic_year",
  },

  {
    key: CUSTOM_DURATION,
    value: CUSTOM_DURATION,
    label: "common:custom",
  },
];

export const getDateFilterOptions = ({ t, selectedAcademicYear }) => {
  const isCurrentAcademicYearSelected = _.get(
    selectedAcademicYear,
    "isCurrentAcademicYear"
  );
  const yearRange = getCurrentAcademicYearLabel();

  const options = isCurrentAcademicYearSelected
    ? _.map(DATE_RANGE_OPTIONS, option => ({
        ...option,
        label:
          option.key === CURRENT_ACADEMIC_YEAR
            ? t(option.label, { yearRange })
            : t(option.label),
      }))
    : [
        {
          key: CUSTOM_DURATION,
          value: CUSTOM_DURATION,
          label: t("common:custom"),
        },
        {
          key: PAST_ACADEMIC_YEARS,
          value: PAST_ACADEMIC_YEARS,
          label: t("behaviour:selected_academic_year_range", {
            yearRange: getPreviousAcademicYearLabel(),
          }),
        },
      ];

  return [
    {
      key: "DATE_RANGE",
      value: "DATE_RANGE",
      label: t("behaviour:filter_by_date"),
      isItemGroup: true,
      options,
    },
  ];
};

const getCustomDateRangeLabel = ({ screen, key, t }) => {
  const state = store.getState();

  const dateRange =
    _.get(state, [
      "behaviourManagement",
      screen,
      "headerFilters",
      "dateRange",
    ]) ?? {};

  const { customStartDate: startDate, customEndDate: endDate } = dateRange;

  if (!startDate || !endDate) {
    return "";
  }

  const start = moment(startDate);
  const end = moment(endDate);

  if (!start.isValid() || !end.isValid()) {
    return "";
  }

  return key === PAST_ACADEMIC_YEARS
    ? t("behaviour:selected_academic_year_range", {
        yearRange: getPreviousAcademicYearLabel(),
      })
    : `${start.format("D MMM, YYYY")} - ${end.format("D MMM, YYYY")}`;
};

export const getSelectedDateRangeLabel = ({ t, key, screen }) => {
  const yearRange = getCurrentAcademicYearLabel();
  const customDateRangeLabel = getCustomDateRangeLabel({ screen, key, t });
  const labelMapping = {
    [LAST_7_DAYS]: t("common:last_7_days"),
    [LAST_30_DAYS]: t("common:last_30_days"),
    [LAST_90_DAYS]: t("common:last_90_days"),
    [LAST_6_MONTHS]: t("common:last_6_months"),
    [CURRENT_ACADEMIC_YEAR]: t("behaviour:current_academic_year", {
      yearRange,
    }),
    [CUSTOM_DURATION]: customDateRangeLabel,
    [PAST_ACADEMIC_YEARS]: customDateRangeLabel,
  };

  return labelMapping[key];
};

export const getDateRangeForCurrentAYOption = () => {
  const currentAYStartDate = getCurrentAcademicYearStartDate();
  const currentAYEndDate = getCurrentAcademicYearEndDate();

  return {
    startDate: getStartOfDay({ date: currentAYStartDate }),
    endDate: getEndOfDay({ date: currentAYEndDate }),
  };
};

export const getDateRange = ({
  dateViewType,
  rangeStartDate,
  rangeEndDate,
}) => {
  const daysToSubtract = dateRangeDaysMap[dateViewType];

  if (daysToSubtract) {
    const today = moment();

    let rangeStart = today
      .clone()
      .startOf("day")
      .subtract(daysToSubtract - 1, "days");

    // ensure startDate is not before academic year start date
    const currentAYStartDate = getStartOfDay({
      date: getCurrentAcademicYearStartDate(),
      shouldFormat: false,
    });

    if (currentAYStartDate.isAfter(rangeStart)) {
      rangeStart = currentAYStartDate;
    }

    rangeStart = rangeStart.format();

    const rangeEnd = today.clone().endOf("day").format();

    return {
      rangeStart,
      rangeEnd,
    };
  }

  if (dateViewType === CURRENT_ACADEMIC_YEAR) {
    const { startDate, endDate } = getDateRangeForCurrentAYOption();
    return {
      rangeStart: getStartOfDay({ date: startDate }),
      rangeEnd: getEndOfDay({ date: endDate }),
    };
  }

  if (
    dateViewType === CUSTOM_DURATION ||
    dateViewType === PAST_ACADEMIC_YEARS
  ) {
    return {
      rangeStart: getStartOfDay({ date: rangeStartDate }),
      rangeEnd: getEndOfDay({ date: rangeEndDate }),
    };
  }

  return {};
};

export const getIsCreateIncidentEnabled = ({
  curriculumProgramIds,
  checkForAssociation = false,
  isStaffAssociatedWithStudent = false,
  applyCurriculumContext = false,
}) => {
  const isRnPEnabledInBehaviour = ACLStore.can(
    [
      ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
      ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
    ],
    "all"
  );

  if (!isRnPEnabledInBehaviour) {
    return true;
  }

  const isBehaviourRnPModuleConsumptionEnabled = getIsBehaviourRnPModuleConsumptionEnabled();

  if (isBehaviourRnPModuleConsumptionEnabled) {
    const controlParameterList = [
      // Account level
      BEHAVIOUR_RNP_FOR_ACCOUNT_CP.CAN_CREATE_INCIDENT,
      // Programme level
      BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_CREATE_INCIDENTS_FOR_ALL,
    ];

    // Push when association check is not required,
    // or when it is required and the staff is associated with the student
    if (!checkForAssociation || isStaffAssociatedWithStudent) {
      controlParameterList.push(
        BEHAVIOUR_RNP_FOR_PROGRAMME_CP.CAN_CREATE_INCIDENTS_FOR_THEIR_STUDENTS
      );
    }

    return _.some(controlParameterList, controlParameter =>
      _.some(curriculumProgramIds, curriculumProgramId =>
        ACLStorage.getPermissionValueByContext(controlParameter, [
          {
            entityId: curriculumProgramId,
            entityType: "CURRICULUM_PROGRAM",
          },
        ])
      )
    );
  }

  return applyCurriculumContext
    ? _.some(curriculumProgramIds, curriculumId => {
        return ACLStorage.getPermissionValueByContext(
          BEHAVIOUR_RNP_PERMISSION.CAN_CREATE_INCIDENT,
          [
            {
              entityId: curriculumId,
              entityType: "CURRICULUM_PROGRAM",
            },
          ]
        );
      })
    : ACLStorage.getPermissionValue(
        BEHAVIOUR_RNP_PERMISSION.CAN_CREATE_INCIDENT
      );
};

export const getNotificationToggleTooltip = ({
  t,
  shouldNotify,
  isDisabled = false,
}) => {
  if (isDisabled) {
    return t("behaviour:disabled_notifications_toggle_for_entity");
  }

  if (shouldNotify) {
    return t("behaviour:toggle_notifications_off");
  }

  return t("behaviour:toggle_notifications_on");
};

export const getShareWithEntityRoleIdFromKey = key => {
  const withoutPrefix = _.replace(key, "ROLE_", "");

  return _.first(_.split(withoutPrefix, ":"));
};
